import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  ArchiveIssueSourceDocument,
  ClearDefaultIssueSourceDocument,
  connectionRows,
  CreateIssueSourceDocument,
  DeleteIssueSourceDocument,
  GetIssueSourcesDocument,
  type IssueSourceRow,
  SetDefaultIssueSourceDocument,
  UnarchiveIssueSourceDocument,
  UpdateIssueSourceDocument,
} from "#app/lib/data";
import {
  addIssueSourceToCache,
  removeIssueSourceFromCache,
} from "./issue-source-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";

export type IssueSource = IssueSourceRow;

const ISSUE_SOURCES_QUERY_VARS = { limit: 50, orderBy: { name: 1 } };

export function useIssueSources() {
  const { data, loading, error } = useQuery(GetIssueSourcesDocument, {
    variables: ISSUE_SOURCES_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const issueSources = useMemo<IssueSource[]>(
    () => connectionRows(data?.issueSources),
    [data],
  );

  return {
    issueSources,
    isLoading: loading,
    error,
  };
}

export function useIssueSourceMutations() {
  const [createMutation] = useMutation(CreateIssueSourceDocument);
  const [updateMutation] = useMutation(UpdateIssueSourceDocument);
  const [archiveMutation] = useMutation(ArchiveIssueSourceDocument);
  const [unarchiveMutation] = useMutation(UnarchiveIssueSourceDocument);
  const [deleteMutation] = useMutation(DeleteIssueSourceDocument);
  const [setDefaultMutation] = useMutation(SetDefaultIssueSourceDocument);
  const [clearDefaultMutation] = useMutation(ClearDefaultIssueSourceDocument);

  const createIssueSource = useCallback(
    async (name: string) => {
      try {
        const { data } = await createMutation({
          variables: { input: { name } },
          update(cache, { data }) {
            const result = data?.createIssueSource;
            if (result?.__typename !== "IssueSource") return;
            addIssueSourceToCache(cache, result);
          },
        });
        const result = data?.createIssueSource;
        if (result?.__typename === "IssueSource") {
          toastManager.add({ title: "Source created", type: "success" });
          return result;
        }
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to create source", type: "error" });
          throw new Error(result.message);
        }
        toastManager.add({ title: "Failed to create source", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create source");
        return null;
      }
    },
    [createMutation],
  );

  const updateIssueSource = useCallback(
    async (id: string, name: string) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input: { name } },
        });
        const result = data?.updateIssueSource;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to update source", type: "error" });
          throw new Error(result.message);
        }
        if (result?.__typename === "IssueSource") {
          toastManager.add({ title: "Source updated", type: "success" });
          return result;
        }
        toastManager.add({ title: "Failed to update source", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update source");
        return null;
      }
    },
    [updateMutation],
  );

  const toggleIssueSource = useCallback(
    async (source: IssueSource) => {
      try {
        if (source.archived) {
          await unarchiveMutation({ variables: { id: source.id } });
        } else {
          await archiveMutation({ variables: { id: source.id } });
        }
        toastManager.add({ title: source.archived ? "Source enabled" : "Source disabled", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update source");
      }
    },
    [archiveMutation, unarchiveMutation],
  );

  const deleteIssueSource = useCallback(
    async (source: IssueSource) => {
      try {
        await deleteMutation({
          variables: { id: source.id },
          update(cache, { data }) {
            const result = data?.deleteIssueSource;
            if (result?.__typename !== "IssueSource") return;
            removeIssueSourceFromCache(cache, result);
          },
        });
        toastManager.add({ title: "Source deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete source");
      }
    },
    [deleteMutation],
  );

  const setDefaultIssueSource = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation({
          variables: { id },
          refetchQueries: ["GetIssueSources"],
        });
        toastManager.add({ title: "Default source updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update default source");
      }
    },
    [setDefaultMutation],
  );

  const clearDefaultIssueSource = useCallback(
    async () => {
      try {
        await clearDefaultMutation({
          refetchQueries: ["GetIssueSources"],
        });
        toastManager.add({ title: "Default source cleared", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to clear default source");
      }
    },
    [clearDefaultMutation],
  );

  return {
    createIssueSource,
    updateIssueSource,
    toggleIssueSource,
    deleteIssueSource,
    setDefaultIssueSource,
    clearDefaultIssueSource,
  };
}
